import type { CSSProperties } from "react";
import { NavLink } from "react-router";
import { useLocale } from "@genius-tracker/i18n/react";
import { BellIcon } from "../layout/NavIcons.js";
import { NotificationBadge } from "./NotificationBadge.js";
import { useUnreadNotificationCount } from "./useNotifications.js";

/**
 * GT-CUSTOMER-NOTIFICATIONS-WEB-INAPP-04 (Wave D) — the top-bar bell.
 *
 * Sprint 60 restored the bell as a header affordance pointing at the
 * preferences page because no inbox existed ("no API exists today").
 * Sprint 3 shipped the backend; this repoints the SAME affordance —
 * same `topbar-notifications-link` test id, same slot — to the new
 * `/notifications` Notification Center and adds the unread badge.
 * Preferences stay reachable at `/me/notifications` (linked from the
 * Center header and the More menu).
 *
 * The unread count rides the shared
 * `["me","notifications","unread-count"]` query: 60 s fallback poll
 * + realtime `notifications.hint` coalesced invalidation
 * (RealtimeProvider). The visible badge is aria-hidden; the count is
 * announced once via the link's `aria-label`.
 *
 * Owner Build-Go decision #1: NO dropdown — the bell navigates to
 * the page.
 */
export function NotificationBell({ style }: { style?: CSSProperties }) {
  const { t } = useLocale();
  const unread = useUnreadNotificationCount();
  const count = unread.data?.unread ?? 0;
  return (
    <NavLink
      to="/notifications"
      aria-label={t("notifications.center.bell.aria", { count })}
      data-testid="topbar-notifications-link"
      style={{ position: "relative", ...style }}
    >
      <BellIcon />
      <NotificationBadge count={count} />
    </NavLink>
  );
}
